import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Banknote,
  BarChart3,
  Bell,
  Calculator,
  CalendarClock,
  Crown,
  Landmark,
  LayoutGrid,
  LineChart,
  Lock,
  ReceiptText,
  RefreshCw,
  Repeat,
  TrendingUp,
  User,
} from "lucide-react";
import { formatEUR, formatEUR0 } from "@/lib/format";
import { LiqoMark } from "@/components/LiqoMark";
import { InsightCard } from "@/components/InsightCard";
import { ChatPanel } from "@/components/ChatPanel";
import { TrialBanner } from "@/components/TrialBanner";

const RAIL_H = "h-[58px]";

const DeskClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <span className="mono text-[10px] tracking-[0.15em] text-[#00FF87]" data-testid="desktop-clock">
      {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}
    </span>
  );
};

const NavItem = ({ icon: Icon, label, onClick, testid, active = false, accent, dot = false, badge = 0 }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`group flex w-full items-center gap-3 rounded-[8px] border px-3 py-2.5 text-left transition-colors ${
      active
        ? "border-[#00FF87]/30 bg-[#00FF87]/[0.06] text-[#00FF87]"
        : "border-transparent text-white/65 hover:border-white/[0.08] hover:bg-white/[0.04] hover:text-white"
    }`}
  >
    <Icon
      size={15}
      className={active ? "text-[#00FF87]" : "text-white/45 group-hover:text-white/75"}
      style={!active && accent ? { color: accent } : undefined}
    />
    <span className="text-[13px] font-light">{label}</span>
    {dot && (
      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#00FF87] shadow-[0_0_6px_rgba(0,255,135,0.8)]" />
    )}
    {badge > 0 && (
      <span
        className="mono ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FF4D4D] px-1 text-[9px] font-semibold leading-none text-black"
        data-testid="desktop-notif-badge"
      >
        {badge > 9 ? "9+" : badge}
      </span>
    )}
  </button>
);

const FeedLoading = () => (
  <div className="space-y-3" data-testid="desktop-feed-loading">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4">
        <div className="h-2.5 w-28 animate-pulse bg-white/[0.06]" />
        <div className="mt-4 h-8 w-32 animate-pulse bg-white/[0.08]" />
        <div className="mt-3 h-3 w-3/4 animate-pulse bg-white/[0.05]" />
        <div className="mt-5 flex gap-2">
          <div className="h-8 flex-1 animate-pulse rounded-[6px] bg-white/[0.05]" />
          <div className="h-8 flex-1 animate-pulse rounded-[6px] bg-white/[0.04]" />
        </div>
      </div>
    ))}
  </div>
);

// Layout "cockpit" desktop: rail sinistro (saldo + navigazione), centro (feed),
// rail destro (Chat CFO dockata). Stesso stile terminale del mobile, proporzioni
// pensate per schermi larghi. Renderizzato SOLO su desktop (un solo albero attivo).
export const HomeDesktop = ({
  balance,
  cards,
  refreshing,
  onRefresh,
  onDismiss,
  onResolve,
  onDetail,
  bankStatus,
  notifUnread = 0,
  subscription,
  isPremium,
  onUpgrade,
  onBank,
  onAnalytics,
  onTax,
  onSubscriptions,
  onNotifications,
  onInvestments,
  onMonthly,
  onMonth,
  onAccount,
  onTransactions,
  onAddCash,
  isFounder = false,
}) => {
  const investments = balance?.investments || 0;
  const hasInvest = investments > 0;
  const netWorth = balance ? balance.net_worth ?? balance.balance : null;
  const monthNet = balance ? balance.month_income - balance.month_spent : 0;
  const up = monthNet >= 0;
  const bankConnected = !!bankStatus?.connected;
  const activeCount = Array.isArray(cards) ? cards.length : 0;

  return (
    <div
      className="flex h-screen w-full overflow-hidden border-x border-white/[0.05] bg-[#050508]"
      data-testid="home-desktop"
    >
      {/* ============ RAIL SINISTRO ============ */}
      <aside className="flex w-[296px] shrink-0 flex-col border-r border-white/[0.05]">
        <div className={`flex ${RAIL_H} items-center gap-2 border-b border-white/[0.05] px-5`}>
          <LiqoMark size={17} />
          <span className="mono text-sm font-light tracking-[0.16em] text-white">
            LIQO<span className="liqo-blink text-[#00FF87]">▌</span>
          </span>
          {isFounder && (
            <span
              data-testid="desktop-founder-badge"
              className="mono flex items-center gap-1 rounded-[4px] border px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-[0.18em]"
              style={{
                borderColor: "rgba(0,255,135,0.45)",
                color: "#00FF87",
                background: "rgba(0,255,135,0.06)",
                boxShadow: "0 0 12px rgba(0,255,135,0.18)",
              }}
            >
              <Crown size={9} /> Founder
            </span>
          )}
          <span className="ml-auto">
            <DeskClock />
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          {/* riepilogo saldo */}
          <div className="rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="desktop-balance">
            <p className="caps-tag text-white/40">{hasInvest ? "Patrimonio netto" : "Saldo netto"}</p>
            <p
              className="mono mt-2 text-[34px] font-light leading-none tracking-[-1.5px] text-[#00FF87] tabular"
              data-testid="desktop-net-balance"
            >
              {balance ? formatEUR(netWorth) : "—"}
            </p>

            {balance?.bank_stale && (
              <button
                onClick={onBank}
                data-testid="desktop-bank-stale-hint"
                className="mono mt-3 flex items-center gap-1.5 rounded-[5px] border border-[#F5A623]/35 bg-[#F5A623]/[0.06] px-2 py-1 text-[10px] tracking-[0.04em] text-[#F5A623] hover:bg-[#F5A623]/[0.12]"
              >
                <AlertTriangle size={11} /> Saldo non riconciliato
              </button>
            )}

            {balance && (
              <>
                <p className="mono mt-3 text-xs tracking-[0.05em]" data-testid="desktop-month-summary">
                  <span className={`liqo-arrow-anim mr-1 ${up ? "text-[#00FF87]" : "text-[#FF4D4D]"}`}>
                    {up ? "↑" : "↓"}
                  </span>
                  <span className={up ? "text-[#00FF87]" : "text-[#FF4D4D]"}>
                    {up ? "+" : "−"}
                    {formatEUR0(Math.abs(monthNet))}
                  </span>
                  <span className="text-white/40"> questo mese</span>
                </p>

                <button
                  onClick={onInvestments}
                  data-testid="desktop-investments-entry"
                  className="mono mt-3 flex w-full items-center gap-1.5 text-left text-[11px] tracking-[0.04em] text-white/40 hover:text-white/70"
                >
                  {hasInvest ? (
                    <>
                      <span>Liquidità {formatEUR0(balance.balance)}</span>
                      <span className="text-white/20">·</span>
                      <span style={{ color: "#2DD4BF" }}>Invest. {formatEUR0(investments)}</span>
                      <span style={{ color: "#2DD4BF" }}>↗</span>
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1" style={{ color: "#2DD4BF", opacity: 0.85 }}>
                      ↗ Aggiungi investimenti
                    </span>
                  )}
                </button>
              </>
            )}
          </div>

          {/* navigazione */}
          <nav className="mt-5 space-y-1" data-testid="desktop-nav">
            <p className="caps-tag px-1 pb-2 text-white/30">Naviga</p>
            <NavItem icon={LayoutGrid} label="Feed insight" testid="desktop-nav-feed" active />
            <NavItem
              icon={Landmark}
              label="Conti & banca"
              testid="desktop-nav-bank"
              onClick={onBank}
              dot={bankConnected}
            />
            <NavItem icon={TrendingUp} label="Investimenti" testid="desktop-nav-investments" onClick={onInvestments} />
            <NavItem icon={CalendarClock} label="Questo mese" testid="desktop-nav-month" onClick={onMonth} accent="#00D4FF" />
            <NavItem icon={LineChart} label="Report mensile" testid="desktop-nav-monthly" onClick={onMonthly} />
            <NavItem icon={BarChart3} label="Analytics" testid="desktop-nav-analytics" onClick={onAnalytics} />

            <p className="caps-tag px-1 pb-2 pt-4 text-white/30">Strumenti</p>
            <NavItem icon={Calculator} label="Fisco & tasse" testid="desktop-nav-tax" onClick={onTax} accent="#F5A623" />
            <NavItem
              icon={Repeat}
              label="Abbonamenti"
              testid="desktop-nav-subscriptions"
              onClick={onSubscriptions}
              accent="#FF7AB6"
            />
            <NavItem icon={ReceiptText} label="Transazioni" testid="desktop-nav-transactions" onClick={onTransactions} />
            <NavItem icon={Banknote} label="Aggiungi contanti" testid="desktop-nav-add-cash" onClick={onAddCash} accent="#00FF87" />
            <NavItem icon={Bell} label="Notifiche" testid="desktop-nav-notifications" onClick={onNotifications} badge={notifUnread} />
          </nav>
        </div>

        {/* footer rail */}
        <div className="flex items-center gap-2 border-t border-white/[0.05] p-4">
          <button
            onClick={onAccount}
            data-testid="desktop-account-button"
            className="flex flex-1 items-center gap-2.5 rounded-[8px] border border-white/[0.08] px-3 py-2.5 text-left text-white/70 hover:border-[#00FF87]/30 hover:text-white"
          >
            <User size={14} className="text-white/50" />
            <span className="text-[12px] font-light">Account</span>
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="desktop-refresh-button"
            aria-label="Aggiorna insight"
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[8px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05] disabled:opacity-50"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </aside>

      {/* ============ COLONNA CENTRALE: FEED ============ */}
      <main className="flex min-w-0 flex-1 flex-col" data-testid="desktop-center-feed">
        <div className={`flex ${RAIL_H} items-center justify-between border-b border-white/[0.05] px-6`}>
          <span className="mono text-sm font-medium tracking-[0.1em] text-[#00FF87]">
            FEED <span className="text-white/25">//</span> INSIGHT
          </span>
          <div className="flex items-center gap-4">
            <span className="caps-tag text-white/30" data-testid="desktop-feed-count">
              {activeCount} {activeCount === 1 ? "attivo" : "attivi"}
            </span>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="desktop-feed-refresh"
              aria-label="Aggiorna"
              className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05] disabled:opacity-50"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-7" data-testid="desktop-feed">
          <div className="mx-auto max-w-[600px]">
            <TrialBanner subscription={subscription} onUpgrade={onUpgrade} />

            {bankStatus && bankStatus.configured && (
              <button
                onClick={onBank}
                data-testid="desktop-connect-bank-cta"
                className="mb-4 flex w-full items-center gap-3 rounded-[12px] border border-[#00FF87]/25 bg-[#0D0D14] p-3.5 text-left shadow-[0_0_0_1px_rgba(0,255,135,0.08)] hover:bg-[#00FF87]/[0.04]"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-[#00FF87]/30 text-[#00FF87]">
                  <Landmark size={16} />
                </span>
                <span className="flex-1">
                  <span className="block text-[13px] font-light text-white/85">
                    {bankConnected ? "Aggiungi o gestisci le tue banche" : "Collega la tua banca reale"}
                  </span>
                  <span className="caps-tag mt-0.5 block text-white/40">
                    {bankConnected
                      ? `${bankStatus.connections?.length || 0} ${
                          (bankStatus.connections?.length || 0) === 1 ? "banca collegata" : "banche collegate"
                        } · sync ogni 6h`
                      : "Open Banking · sync ogni 6h"}
                  </span>
                </span>
                <span className="mono text-xs text-[#00FF87]/70">❯</span>
              </button>
            )}

            {cards === null ? (
              <FeedLoading />
            ) : cards.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col items-center pt-24 text-center"
              >
                <span className="mono text-xl text-[#00FF87]">
                  ❯<span className="liqo-blink">_</span>
                </span>
                <p className="mono mt-5 text-base font-light text-white/90" data-testid="desktop-empty-state">
                  Niente di urgente oggi.
                </p>
                <p className="mt-2 max-w-[280px] text-[13px] font-light text-white/40">
                  L'agente ti avviserà quando ci sarà qualcosa di rilevante.
                </p>
              </motion.div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {cards.map((card, i) => (
                    <InsightCard
                      key={card.id}
                      card={card}
                      index={i}
                      onDismiss={onDismiss}
                      onDetail={onDetail}
                      onResolve={onResolve}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ============ RAIL DESTRO: CHAT CFO DOCKATA ============ */}
      <aside className="flex w-[384px] shrink-0 flex-col border-l border-white/[0.05] bg-[#030305]" data-testid="desktop-right-chat">
        <div className={`flex ${RAIL_H} items-center justify-between border-b border-white/[0.05] px-5`}>
          <div className="flex items-center gap-3">
            <span className="mono text-sm font-medium tracking-[0.1em] text-[#00FF87]">
              CFO <span className="text-white/25">//</span> LIQO
            </span>
            {isPremium && (
              <span className="caps-tag flex items-center gap-1.5 text-[#00FF87]/70">
                <span className="liqo-dot-pulse h-1 w-1 bg-[#00FF87]" />
                Online
              </span>
            )}
          </div>
        </div>

        {isPremium ? (
          <ChatPanel active />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="desktop-chat-locked">
            <span className="flex h-14 w-14 items-center justify-center rounded-[12px] border border-[#00FF87]/40 text-[#00FF87]">
              <Lock size={22} />
            </span>
            <p className="mono mt-5 text-sm font-light text-white/85">Chat CFO bloccata</p>
            <p className="mt-2 max-w-[240px] text-[13px] font-light leading-6 text-white/45">
              Sblocca Liqo Pro per parlare con il tuo CFO AI, che conosce ogni transazione.
            </p>
            <button
              onClick={onUpgrade}
              data-testid="desktop-chat-unlock"
              className="mono mt-5 flex h-11 items-center justify-center gap-2 rounded-[8px] border border-[#00FF87]/50 bg-[#00FF87]/[0.06] px-5 text-[11px] font-medium uppercase tracking-[0.16em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.12)] hover:bg-[#00FF87]/[0.1] active:scale-[0.98]"
            >
              Sblocca Liqo Pro ❯
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};
